'use client';

import React, { useMemo, useState } from "react";
import "./independent-teachers.modules.css"; // global stylesheet
import Navbar from "@components/Navbar";
import Footer from "@components/footer/Footer";

export default function IndependentTeachers() {
  const FORM_ID =
    "1FAIpQLSdHYuRndke6F2KA45iTgKTms_Mzt89Gs7UbmRfiL0eP3Z_GHg";

  const FORM_VIEW_URL = useMemo(
    () => `https://docs.google.com/forms/d/e/${FORM_ID}/viewform`,
    [FORM_ID]
  );

  const openForm = () => {
    window.open(FORM_VIEW_URL, "_blank", "noopener,noreferrer");
  };

  // Accordion state (only one open at a time)
  const [openStep, setOpenStep] = useState(null);

  const steps = [
    {
      title: "STEP 1: APPLY",
      body: (
        <>
          <p>
            Click <strong>Apply now</strong> and complete the application form. Our team will review your
            submission and follow up by email.
          </p>
          <button type="button" className="btn btn-primary" onClick={openForm}>
            Apply now
          </button>
        </>
      ),
    },
    {
      title: "STEP 2: INTERVIEW",
      body: (
        <>
          <p>
            If we see a strong match and there’s demand for your subject, we’ll invite you to a short
            interview to learn more about your teaching approach and class ideas.
          </p>
        </>
      ),
    },
    {
      title: "STEP 3: BACKGROUND CHECK",
      body: (
        <>
          <p>
            You’ll verify your identity and complete a background check with our partner (Checkr). This helps keep PenguinLearn safe.
            and trustworthy for families.
          </p>
        </>
      ),
    },
    {
      title: "STEP 4: ONBOARDING",
      body: (
        <>
          <p>
            We’ll guide you through setup, best practices, and what to expect when publishing classes and
            communicating with families.
          </p>
          <p style={{ marginTop: 10 }}>
            As we grow, we’ll keep expanding teacher support—live feedback sessions, training resources,
            dedicated staff guidance, professional development, mentorship, and more.
          </p>
        </>
      ),
    },
    {
      title: "STEP 5: TEACHER PROFILE SETUP",
      body: (
        <>
          <p>
            Build your teacher profile so families can quickly understand who you are and what you teach
            (photo, bio, experience, and subjects).
          </p>
        </>
      ),
    },
    {
      title: "STEP 6: PUBLISH YOUR FIRST COURSE",
      body: (
        <>
          <p>
            Create your first course with your schedule, pricing, and class size. Our team will review it to
            confirm it meets our quality and safety standards before it goes live.
          </p>
        </>
      ),
    },
  ];

  const toggleStep = (idx) => {
    setOpenStep((prev) => (prev === idx ? null : idx));
  };

  return (
    <>
      <Navbar />

      {/* Hero */}
      <header className="hero-section">
        <div className="container">
          <h1 className="hero-title">
            Share your knowledge and teach <span className="accent">on your terms</span>
          </h1>

          <p className="subtitle">
            Create an account to get started. You’ll receive an automatic verification email, then you can
            submit the application form. A team member will respond within 24 business hours.
          </p>

          <ul className="hero-stats" aria-label="Platform highlights">
            <li>
              <span className="stat-value">20%</span>
              <span className="stat-label">Platform fee</span>
            </li>
            <li>
              <span className="stat-value">Global</span>
              <span className="stat-label">Learners</span>
            </li>
          </ul>
        </div>
      </header>

      <main>
        {/* Why Teach */}
        <section id="why" className="why-teach" aria-labelledby="why-title">
          <div className="container">
            <h2 id="why-title">Why teach with PenguinLearn?</h2>
            <p className="section-lead">
              A simple platform built for independent teachers—fair payouts, flexible schedules, and
              learners who are excited to be there.
            </p>

            <div className="features">
              <article className="feature">
                <img
                  src="/images/icon-assessment.svg"
                  alt=""
                  loading="lazy"
                  decoding="async"
                  width="64"
                  height="64"
                />
                <h3>Teach motivated learners</h3>
                <p>Teach kids who chose your class and want to learn.</p>
              </article>

              <article className="feature">
                <img
                  src="/images/calendar-icon.svg"
                  alt=""
                  loading="lazy"
                  decoding="async"
                  width="64"
                  height="64"
                />
                <h3>Set your own schedule</h3>
                <p>Create sections that work for you and adjust anytime.</p>
              </article>

              <article className="feature">
                <img
                  src="/images/converted_image_3.webp"
                  alt=""
                  loading="lazy"
                  decoding="async"
                  width="64"
                  height="64"
                />
                <h3>Earn flexibly</h3>
                <p>Choose your pricing and keep more with a low, transparent fee.</p>
              </article>

              <article className="feature">
                <img
                  src="/images/converted_image_2.webp"
                  alt=""
                  loading="lazy"
                  decoding="async"
                  width="64"
                  height="64"
                />
                <h3>Less admin work</h3>
                <p>We help with payments, logistics, and support so you can focus on teaching.</p>
              </article>
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="why-teach" aria-labelledby="how-it-works-title">
          <div className="container">
            <h2 id="how-it-works-title">How teaching works</h2>

            <p className="section-lead">
              You set your schedule, pricing, and the minimum/maximum number of learners per course. To keep
              classes affordable for families, we recommend pricing within <strong>$10–$30 per class</strong>.
              For the best learning experience, we also recommend capping each section at{" "}
              <strong>8 learners</strong>. Teachers earn <strong>80%</strong> of the payments from every class
              taught.
            </p>

            <p className="section-lead" style={{ marginTop: 12 }}>
              Our marketplace is easy to navigate, so families can quickly enroll in courses they love. We
              also promote courses through our social media platforms and community groups to help connect
              teachers with excited learners.
            </p>

            <p className="section-lead" style={{ marginTop: 12 }}>
              To support teacher success, we invite new teachers based on demand. Applications are always
              open, but onboarding may pause at times so the marketplace isn’t flooded with more classes than
              learners can fill.
            </p>
          </div>
        </section>

        {/* Steps Accordion */}
        <section className="why-teach" aria-labelledby="steps-title">
          <div className="container">
            <h2 id="steps-title">How to get started</h2>
            <p className="section-lead">
              Here’s what the process looks like—from application to publishing your first course.
            </p>

            <div className="steps-accordion" role="region" aria-label="Getting started steps">
              {steps.map((step, idx) => {
                const isOpen = openStep === idx;
                return (
                  <div className={`step-item ${isOpen ? "open" : ""}`} key={step.title}>
                    <button
                      type="button"
                      className="step-trigger"
                      onClick={() => toggleStep(idx)}
                      aria-expanded={isOpen}
                      aria-controls={`step-panel-${idx}`}
                    >
                      <span className="step-title">{step.title}</span>
                      <span className="step-icon" aria-hidden="true">
                        {isOpen ? "−" : "+"}
                      </span>
                    </button>

                    <div
                      id={`step-panel-${idx}`}
                      className="step-panel"
                      role="region"
                      aria-label={step.title}
                      style={{ display: isOpen ? "block" : "none" }}
                    >
                      {step.body}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Teacher Requirements */}
        <section className="why-teach" aria-labelledby="requirements-title">
          <div className="container">
            <h2 id="requirements-title">Teacher Requirements</h2>

            <p className="section-lead">
              We do not require a formal teaching license, degree, or credentials. However, we{" "}
              <strong>highly recommend</strong> that you are pursuing a related field or have meaningful
              experience in the subject you plan to teach.
            </p>

            <p className="section-lead" style={{ marginTop: 12 }}>
              Experience and expertise can come from many paths. We’re looking for teachers who are
              knowledgeable, kind, reliable, and excited to spark curiosity in learners.
            </p>

            <p className="section-lead" style={{ marginTop: 12 }}>
              To help ensure high-quality and trustworthy educational experiences, teachers must meet the
              following requirements:
            </p>

            <ul className="requirements-list" aria-label="Teacher requirements">
              <li>18+ and a U.S. resident.</li>
              <li>Reliable computer/tablet and fast internet.</li>
              <li>Comfortable leading online classes.</li>
              <li>Background check (with Checkr).</li>
              <li>Follow our course content policy and community standards.</li>
              <li>Confident working with children.</li>
            </ul>
          </div>
        </section>

        {/* FAQ */}
        <section className="why-teach" aria-labelledby="faq-title">
          <div className="container">
            <h2 id="faq-title">FAQ</h2>

            <details className="faq-item">
              <summary className="faq-summary">
                Q: How can I enroll more learners in my courses?
              </summary>
              <div className="faq-body">
                <p>
                  We actively share available courses through our social media channels, our community Facebook
                  group, and weekly highlights.
                </p>
                <p>We also encourage teachers to share their courses with their own communities.</p>
                <p>
                  The best results come from a combined effort—our team promoting classes plus teachers sharing
                  their listings.
                </p>
              </div>
            </details>

            <details className="faq-item">
              <summary className="faq-summary">
                Q: How are teachers paid &amp; what can I earn?
              </summary>
              <div className="faq-body">
                <p>
                  Teachers earn <strong>80%</strong> of the set class price.
                </p>
                <p>
                  Earnings from individual class meetings are paid 7–13 days after the class ends. This allows
                  time for refunds or issues to be resolved. Payroll runs weekly, and teachers can select how
                  they’d like to be paid.
                </p>
                <p>
                  Discounts and promotions may be offered occasionally.
                  <strong> Discounts offered by PenguinLearn do not affect teachers&apos; pay.</strong>
                </p>
              </div>
            </details>
          </div>
        </section>

        {/* Final CTA */}
        <section className="apply" aria-labelledby="apply-title">
          <div className="container">
            <h2 id="apply-title">Ready to get started?</h2>
            <p className="section-lead" style={{ marginTop: 8 }}>
              Open the application form and we’ll follow up by email.
            </p>

            <div className="cta-row" style={{ marginTop: 18 }}>
              <button type="button" className="btn btn-primary" onClick={openForm}>
                Ready to get started as a PenguinLearn teacher?
              </button>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}